import { dia } from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { SelectionType } from 'guideline/domain';
import { of, switchMap, take } from 'rxjs';
import { ComponentConverter } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  FlowchartActionDto,
  FlowchartContainerDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartSubflowDto,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { BasicNode } from './BasicNode';
import { IFlowchartNode } from './IFlowchartNode';
import { FlowchartSidebarStoreService } from '../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { FlowchartGenericDto } from '../infrastructure/base/flowchart-events.service';
import { ContainerNodeService } from '../infrastructure/operations/container-node.service';
import OperationsService from '../infrastructure/operations/operations.service';
import SubflowService from '../infrastructure/operations/subflow.service';

export class FlowchartContainerNode extends BasicNode<FlowchartContainerDto> implements IFlowchartNode {
  constructor(element: FlowchartContainerDto) {
    super(element);
  }

  addOutgoingLink(link: FlowchartLinkDto): void {
    this.element.outgoingLinks = this.element.outgoingLinks || [];
    this.element.outgoingLinks.push(link);
  }

  removeOutgoingLink(linkIndex: number): void {
    this.element.outgoingLinks.splice(linkIndex, 1);
  }

  addToGraph(
    graph: dia.Graph,
    paper: dia.Paper,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService: TranslocoService,
    allowEvents = false,
  ): void {
    this.addContainerToGraph(paper, graph, operationsService, translocoService, null, allowEvents);
    operationsService.addElementTags(this.element);
  }

  setPropertiesForInit(element: joint.dia.Cell): void {
    super.setPropertiesForInit(element);
    this.element.elementType = FlowchartElementType.FlowchartContainer;
    if (element.attributes.isContainerSubComponent) {
      this.element.parentId = element.attributes.parentContainerId;
    }
  }

  async setPropertiesAfterSearch(
    definition: ComponentDto | ProposedComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    componentConverter: ComponentConverter,
    selectedElementView?: joint.dia.ElementView,
    translocoService?: TranslocoService,
    flowchartSidebarStoreService?: FlowchartSidebarStoreService,
  ): Promise<void> {
    const copiedObject: FlowchartGenericDto = JSON.parse(JSON.stringify(this.element));

    of(definition)
      .pipe(
        switchMap((definition) => {
          if (definition.code.startsWith('PC-')) {
            return of(componentConverter.convertProposedComponentToAction(definition));
          } else {
            return componentConverter.convertActivityToAction(definition, medicationRequests, this.element.uniqueId);
          }
        }),
        take(1),
      )
      .subscribe((convertedDefinition) => {
        copiedObject.guidelineAction = convertedDefinition;
        copiedObject.guidelineActionId = copiedObject.guidelineAction.id;

        if (selectedElementView) {
          copiedObject.width = selectedElementView.model.attributes.size.width;
          copiedObject.height = selectedElementView.model.attributes.size.height;
        }

        flowchartSidebarStoreService.setDto({
          element: copiedObject,
          type: SelectionType.Flowchart,
        });
      });
  }

  private addChildrenAndSelectionBehaviorToContainer(
    operationsService: OperationsService,
    paper: joint.dia.Paper,
    graph: joint.dia.Graph,
    container: joint.dia.Cell,
    translocoService: TranslocoService,
    subflowDto?: FlowchartSubflowDto,
    allowEvents = false,
  ): joint.dia.Cell {
    const element = subflowDto ? subflowDto : this.element;
    const selectionBehavior = element.guidelineAction?.selectionBehaviorForChildren;
    const subElements = element.children;
    if (subElements) {
      subElements.forEach((el, index) => {
        const title = this.getTitle(el, translocoService);
        const description = this.getDescription(el);
        let subElement;
        if (el.elementType === FlowchartElementType.FlowchartAction) {
          subElement = el as FlowchartActionDto;
        } else {
          subElement = el as FlowchartDecisionNodeDto;
        }
        const subComponent = ContainerNodeService.createContainerSubComponent(paper, graph, {
          text: title,
          size: element.width,
          type: subElement.elementType,
          id: subElement.id,
          uniqueId: subElement.uniqueId,
          allowEvents,
          colorPresetId: subElement.colorPresetId,
        });

        subComponent.set('parentContainerId', element.id);
        operationsService.addSubComponentToContainer(element.id, subComponent);
        operationsService.setClickableForSmartbook(subElement.id, description);
        operationsService.addElementTags(subElement);
        if (el?.showDescription) {
          if (description) {
            operationsService.initElementsDescription(subElement.id, description);
          }
        }
        //only add selection behavior in between subactions
        if (index !== subElements.length - 1 && subComponent.attributes.position.x !== 0) {
          const containerSelectionBehavior = ContainerNodeService.createContainerSelectionBehavior(
            selectionBehavior,
            translocoService,
            subComponent.attributes.position.x,
            subComponent.attributes.position.y,
            subComponent.attributes.size.height,
            subComponent.attributes.size.width,
          );
          graph.addCell(containerSelectionBehavior);
          container.embed(containerSelectionBehavior);
        }
      });
    }
    return container;
  }

  private getDescription(element: FlowchartContainerDto | FlowchartActionDto | FlowchartDecisionNodeDto): string {
    if (element.elementType === FlowchartElementType.FlowchartContainer) {
      const container = element as FlowchartContainerDto;
      return container.guidelineAction?.description;
    } else if (element.elementType === FlowchartElementType.FlowchartAction) {
      const subaction = element as FlowchartActionDto;
      return subaction.guidelineAction?.description;
    } else if (element.elementType === FlowchartElementType.FlowchartDecisionNode) {
      const subdecision = element as FlowchartDecisionNodeDto;
      return subdecision.guidelineCriteria?.description || subdecision.guidelineObservation?.description || 'no description';
    }
  }

  private getTitle(
    element: FlowchartContainerDto | FlowchartActionDto | FlowchartDecisionNodeDto,
    translocoService: TranslocoService,
  ): string {
    if (element.elementType === FlowchartElementType.FlowchartContainer) {
      const container = element as FlowchartContainerDto;
      return container.guidelineAction?.title;
    } else if (element.elementType === FlowchartElementType.FlowchartAction) {
      const subaction = element as FlowchartActionDto;
      return subaction.guidelineAction?.title;
    } else if (element.elementType === FlowchartElementType.FlowchartDecisionNode) {
      const subdecision = element as FlowchartDecisionNodeDto;
      return subdecision.guidelineCriteria?.title || subdecision.guidelineObservation?.title;
    } else {
      return translocoService.translate('SetTitle');
    }
  }

  private addContainerToGraph(
    paper: joint.dia.Paper,
    graph: joint.dia.Graph,
    operationsService: OperationsService,
    translocoService: TranslocoService,
    subflowEl?: FlowchartSubflowDto,
    allowEvents = false,
  ): joint.dia.Cell {
    const element = subflowEl ? subflowEl : this.element;
    const container = ContainerNodeService.createContainer(paper, {
      x: element.positionX,
      y: element.positionY,
      allowEvents,
      size: { width: element.width, height: element.height },
      id: element.id,
      uniqueId: this.element.uniqueId,
      colorPresetId: this.element.colorPresetId,
    });

    graph.addCell(container);

    const title = this.getTitle(element, translocoService);
    const description = this.getDescription(element);
    const containerHeader = ContainerNodeService.createContainerHeader(paper, {
      text: title,
      x: container.attributes.position.x,
      y: container.attributes.position.y,
      colorPresetId: element.colorPresetId,
    });

    graph.addCell(containerHeader);
    container.embed(containerHeader);
    operationsService.addTooltip(containerHeader.id as string);
    operationsService.setClickableForSmartbook(element.id, description);
    operationsService.setClickableForSmartbook(containerHeader.id, description);
    this.addChildrenAndSelectionBehaviorToContainer(operationsService, paper, graph, container, translocoService, subflowEl, allowEvents);
    return container;
  }
}
